/*
 * سدر الخليج (STR-83): منتقي الدولة.
 * - البحث في قائمة الدول (يطبّع العربية: التشكيل والهمزات والتاء المربوطة والألف المقصورة و«ال»).
 * - الأسهم تتخطى الدول المخفية بالبحث، وEnter في مربع البحث يختار الدولة إن بقيت واحدة.
 * - في كل المنتقيات (save) الضغط على دولة أو لغة يحدد فقط، والانتقال بـ«حفظ» المعطّل حتى يتغير شيء، والإغلاق دون حفظ
 *   يعيد ما كان. وcombined: اللغة والدولة في نافذة واحدة.
 * - تحديد دولة الزائر تلقائيًا مرة واحدة في المتصفح، من اقتراح Shopify (browsing_context_suggestions)،
 *   ما لم يختر الزائر دولته بنفسه. لا يعمل لمحركات البحث ولا في محرر القالب.
 * - STR-85: بطاقة اقتراح اللغة أسفل الصفحة لمن لغة جهازه الأولى تخالف لغة الصفحة، من اقتراح Shopify نفسه.
 *   لا تحويل تلقائي للغة.
 * - STR-90: كل منتقٍ يُرسم بدولته الحالية وحدها، وتُنسخ الدول الباقية من قائمة الدول المشتركة (#sedr-country-options،
 *   يرسمها الرأس مرة واحدة) أول ما يهمّ الزائر بفتحه، ويوضع مصدر علم كل دولة عند النسخ.
 * - STR-116: دول الخليج الست أول القائمة بعد الدولة الحالية، وخط يفصلها عن الباقي أبجديًا.
 * - STR-138: الدولة المركّزة بالأسهم تظهر كاملة بين مربع البحث والتذييل الثابت.
 */

const OPTIONS_ID = 'sedr-country-options';

const normalize = (text) =>
  String(text || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[ً-ٰٟـ]/g, '')
    .replace(/[أإآٱ]/g, 'ا')
    .replace(/ة/g, 'ه')
    .replace(/ى/g, 'ي')
    .replace(/ؤ/g, 'و')
    .replace(/ئ/g, 'ي')
    .replace(/(^|\s)ال(?=\S)/g, '$1')
    .replace(/[()\-–,.]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

// أسماء أخرى يبحث بها الزوار عن الدول الأعلى طلبًا وغيرها
const ALIASES = {
  SA: 'ksa saudi السعوديه',
  AE: 'uae emirates dubai امارات دبي',
  KW: 'kuwait',
  QA: 'qatar',
  BH: 'bahrain',
  OM: 'oman',
  US: 'usa america امريكا',
  GB: 'uk britain england بريطانيا انجلترا انكلترا لندن',
  DE: 'المانيا germany',
  NL: 'holland هولندا',
  KR: 'korea كوريا',
  TR: 'turkey turkiye تركيا',
};

if (!customElements.get('sedr-country-picker')) {
  class SedrCountryPicker extends HTMLElement {
    connectedCallback() {
      if (this._abort && !this._abort.signal.aborted) return;
      this._abort = new AbortController();
      const previousListbox = this.listbox;

      this.search = this.querySelector('input[type="search"]');
      this.listbox = this.querySelector('x-listbox');
      this.empty = this.querySelector('.sedr-country-picker__empty');
      if (!this.search || !this.listbox) {
        this._abort.abort();
        return;
      }
      if (previousListbox && previousListbox !== this.listbox) {
        this._filled = false;
        this._clonedOptions = [];
      }
      this.indexOptions();
      this._clonedOptions = (this._clonedOptions || []).filter((option) => this.contains(option));
      this._clonedOptions.forEach((option) => this.bindClonedOption(option));

      // الدول الباقية تُنسخ قبل أن تُفتح النافذة: عند المرور على الزر أو التركيز عليه أو لمسه، وقبل معالج القالب للنقر
      const popover = this.closest('x-popover');
      const trigger = popover?.id && document.querySelector(`[aria-controls="${CSS.escape(popover.id)}"]`);
      const fill = () => this.fill();
      if (trigger) {
        ['pointerenter', 'focus', 'touchstart'].forEach((type) => this.listen(trigger, type, fill, { passive: true }));
        this.listen(trigger, 'click', fill, true);
      }
      this.listen(popover, 'dialog:after-show', fill);

      this.listen(this.search, 'input', () => this.filter());
      this.listen(this.search, 'keydown', (event) => this.onSearchKeyDown(event));
      this.listen(this.listbox, 'keydown', (event) => this.onListKeyDown(event), true);

      if (this.hasAttribute('save')) this.setupCombined();
      if (popover) this.fitKeyboard(popover);

      // تُعاد القائمة كاملة عند إغلاق النافذة، ويُلغى ما حُدّد ولم يُحفظ
      this.listen(popover, 'dialog:after-hide', () => {
        if (this.search.value) {
          this.search.value = '';
          this.filter();
        }
        if (this.combined) this.resetCombined();
      });
    }

    // إعادة رسم الأقسام قد تفصل العنصر ثم تعيده: تُلغى مستمعاته الخارجية ويُهيّأ من جديد.
    disconnectedCallback() {
      if (this.combined) this.resetCombined();
      if (this.search?.value) {
        this.search.value = '';
        this.filter();
      }
      this._stopKeyboard?.();
      this._stopKeyboard = null;
      this._abort?.abort();
      this.combined = null;
    }

    listen(target, type, listener, options = {}) {
      const settings = typeof options === 'boolean' ? { capture: options } : options;
      target?.addEventListener(type, listener, { ...settings, signal: this._abort.signal });
    }

    bindClonedOption(option) {
      this.listen(option, 'click', () => {
        this.listbox.setAttribute('aria-activedescendant', option.id);
        option.dispatchEvent(new CustomEvent('listbox:select', { bubbles: true, detail: { value: option.value } }));
      });
    }

    /* لوحة المفاتيح في الجوال تغطي أسفل الشاشة ولا ترفع النافذة السفلية، فكان مربع البحث يختفي تحتها. فما دامت ظاهرة
       تنزل النافذة إلى أسفل الجزء الظاهر من الشاشة (visualViewport) ويقصر ارتفاعها بقدره، كما في الماسح (STR-48) */
    fitKeyboard(popover) {
      const viewport = window.visualViewport;
      if (!viewport) return;
      const mobile = window.matchMedia('screen and (max-width: 999px)');
      let controller = null;
      const fit = () => {
        const keyboard = document.documentElement.clientHeight - viewport.height > 120;
        popover.toggleAttribute('data-sedr-keyboard', keyboard);
        popover.style.setProperty('--sedr-vv-bottom', `${viewport.offsetTop + viewport.height}px`);
        popover.style.setProperty('--sedr-vv-height', `${viewport.height}px`);
      };
      const start = () => {
        if (controller || !mobile.matches) return;
        controller = new AbortController();
        viewport.addEventListener('resize', fit, { signal: controller.signal });
        viewport.addEventListener('scroll', fit, { signal: controller.signal });
        fit();
      };
      this.listen(popover, 'dialog:after-show', start);
      this.listen(this.search, 'focus', start);
      const stop = () => {
        controller?.abort();
        controller = null;
        popover.removeAttribute('data-sedr-keyboard');
        popover.style.removeProperty('--sedr-vv-bottom');
        popover.style.removeProperty('--sedr-vv-height');
      };
      this._stopKeyboard = stop;
      this.listen(popover, 'dialog:after-hide', stop);
    }

    indexOptions() {
      this.options = Array.from(this.querySelectorAll('[role="option"]'));
      this.index = this.options.map((option) =>
        normalize(`${option.textContent} ${option.value} ${ALIASES[option.value] || ''}`)
      );
    }

    // ينسخ الدول الباقية من القائمة المشتركة مرة واحدة. إن كان x-listbox قد عمل قبلها فهو لا يربط الخيارات الجديدة، فتُربط
    // هنا كما يربطها القالب: معرّف لكل خيار، والنقر يحدده (aria-activedescendant) ويطلق listbox:select
    fill() {
      if (this._filled || !this.listbox) return;
      const source = document.getElementById(OPTIONS_ID);
      if (!source) return;
      this._filled = true;
      const base = source.dataset.flagBase || '';
      const present = new Set(this.options.map((option) => option.value));
      const bind = Boolean(customElements.get('x-listbox'));
      const fragment = document.createDocumentFragment();

      source.content.querySelectorAll('[role="option"]').forEach((item) => {
        if (present.has(item.value)) return;
        const option = item.cloneNode(true);
        const flag = option.querySelector('img[data-flag]');
        if (flag && base) flag.src = `${base}${flag.dataset.flag}.svg`;
        if (bind) {
          option.id = 'option-' + (crypto.randomUUID ? crypto.randomUUID() : Math.floor(Math.random() * 1e9));
          this._clonedOptions.push(option);
          this.bindClonedOption(option);
        }
        fragment.appendChild(option);
      });

      this.listbox.appendChild(fragment);

      // دول الخليج أول القائمة (sedr-country-options.liquid): الحالية منها تنضم إليها، والخط تحت آخرها
      const current = this.listbox.querySelector('.sedr-country-option--current');
      if (current && source.content.querySelector(`.sedr-country-option--pinned[value="${CSS.escape(current.value)}"]`)) {
        current.classList.add('sedr-country-option--pinned');
      }
      const pinned = this.listbox.querySelectorAll('.sedr-country-option--pinned');
      pinned[pinned.length - 1]?.classList.add('sedr-country-option--pinned-end');

      this.indexOptions();
      if (this.search?.value) this.filter();
    }

    setupCombined() {
      const form = this.querySelector('form');
      this.combined = {
        form,
        country: this.listbox.querySelector('input[type="hidden"][name="country_code"]'),
        locale: form?.querySelector('input[type="hidden"][name="locale_code"]'),
        languages: Array.from(this.querySelectorAll('.sedr-loc-segment button')),
        save: this.querySelector('.sedr-loc-save'),
      };
      if (!form || !this.combined.country || !this.combined.locale || !this.combined.save) {
        this.combined = null;
        return;
      }
      this.combined.initialCountry = this.combined.country.value;
      this.combined.initialLocale = this.combined.locale.value;

      this.combined.languages.forEach((button) =>
        this.listen(button, 'click', () => this.selectLanguage(button.value))
      );

      this.listen(this.listbox, 'listbox:change', () => this.updateSave());

      // دولة بعد البحث وEnter أو بالنقر: تُحدَّد ويُنقل التركيز إلى «حفظ» إن تغيّر شيء
      this.listen(this.listbox, 'listbox:select', () => {
        if (!this.combined.save.disabled) this.combined.save.focus();
      });

      this.listen(form, 'submit', (event) => {
        if (!this.changed) {
          // لا تغيير: تُغلق النافذة فقط
          event.preventDefault();
          event.stopImmediatePropagation();
          this.closest('x-popover')?.hide?.();
          return;
        }
      });
    }

    get changed() {
      const c = this.combined;
      return c.country.value !== c.initialCountry || c.locale.value !== c.initialLocale;
    }

    // «حفظ» لا يعمل إلا بعد تغيير حقيقي، ويعود معطّلًا إن رجع الزائر إلى اختياره الأول
    updateSave() {
      this.combined.save.disabled = !this.changed;
    }

    selectLanguage(code) {
      this.combined.locale.value = code;
      this.combined.languages.forEach((button) => button.setAttribute('aria-pressed', String(button.value === code)));
      this.updateSave();
    }

    resetCombined() {
      const c = this.combined;
      this.selectLanguage(c.initialLocale);
      if (c.country.value !== c.initialCountry) {
        c.country.value = c.initialCountry;
        c.country.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.updateSave();
    }

    get visibleOptions() {
      return this.options.filter((option) => !option.hidden);
    }

    filter() {
      // «ال» وحدها بداية أغلب الأسماء العربية: لا تُصفّى بها القائمة
      const query = normalize(this.search.value).replace(/^ال$/, '');
      let count = 0;

      this.options.forEach((option, i) => {
        const match = !query || this.index[i].includes(query);
        option.hidden = !match;
        if (match) count++;
      });

      this.toggleAttribute('filtering', query !== '');
      if (this.empty) this.empty.textContent = count > 0 ? '' : this.empty.dataset.emptyText;
    }

    /* STR-138: رأس البحث والتذييل ملتصقان داخل منطقة التمرير، فتمرير المتصفح عند التركيز كان يترك الدولة المركّزة
       خلف أحدهما. يُركَّز عليها بلا تمرير، ثم تُمرَّر القائمة بقدر ما يُظهرها كاملة بين البحث والتذييل */
    focusOption(option) {
      if (!option) return;
      option.focus({ preventScroll: true });

      // منطقة التمرير قد تكون داخل الجذر الظلي لنافذة القالب، فيُصعد إليها عبر المنفذ (slot)
      const parentOf = (element) => element.assignedSlot || element.parentElement || element.getRootNode().host;
      let scroller = parentOf(option);
      while (scroller && scroller !== document.body) {
        const overflow = getComputedStyle(scroller).overflowY;
        if ((overflow === 'auto' || overflow === 'scroll') && scroller.scrollHeight > scroller.clientHeight) break;
        scroller = parentOf(scroller);
      }
      if (!scroller || scroller === document.body) {
        option.scrollIntoView({ block: 'nearest' });
        return;
      }

      const gap = 8;
      const box = scroller.getBoundingClientRect();
      const head = this.querySelector('.sedr-country-picker__head');
      const footer = this.closest('x-popover')?.querySelector('.sedr-loc-footer');
      const top = Math.max(box.top, head?.getClientRects().length ? head.getBoundingClientRect().bottom : box.top) + gap;
      const bottom =
        Math.min(box.bottom, footer?.getClientRects().length ? footer.getBoundingClientRect().top : box.bottom) - gap;
      const rect = option.getBoundingClientRect();

      if (rect.top < top) scroller.scrollTop -= top - rect.top;
      else if (rect.bottom > bottom) scroller.scrollTop += rect.bottom - bottom;
    }

    onSearchKeyDown(event) {
      const visible = this.visibleOptions;

      if (event.key === 'ArrowDown' && visible.length) {
        event.preventDefault();
        this.focusOption(visible[0]);
      } else if (event.key === 'Enter') {
        event.preventDefault();
        if (visible.length === 1) visible[0].click();
        else this.focusOption(visible[0]);
      }
    }

    // يحل محل أسهم القالب حتى لا تقف على دولة مخفية
    onListKeyDown(event) {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

      const visible = this.visibleOptions;
      const current = visible.indexOf(document.activeElement);
      event.preventDefault();
      event.stopPropagation();

      if (event.key === 'ArrowDown') {
        this.focusOption(visible[Math.min(current + 1, visible.length - 1)]);
      } else if (current <= 0) {
        this.search.focus();
      } else {
        this.focusOption(visible[current - 1]);
      }
    }
  }

  customElements.define('sedr-country-picker', SedrCountryPicker);
}

/* ---------------- تحديد الدولة تلقائيًا ---------------- */

const AUTO_KEY = 'sedr-country-auto';
const FORM_SELECTOR = 'form[id^="localization-form-country"]';

const storage = (() => {
  try {
    const s = window.localStorage;
    s.setItem(AUTO_KEY + '-test', '1');
    s.removeItem(AUTO_KEY + '-test');
    return s;
  } catch (e) {
    return null;
  }
})();

const remember = (value) => {
  try {
    storage?.setItem(AUTO_KEY, value);
  } catch (e) {}
};

const autoDetect = async () => {
  const shopify = window.Shopify || {};
  const agent = navigator.userAgent || '';

  if (!storage || storage.getItem(AUTO_KEY)) return;
  if (navigator.webdriver || shopify.designMode || !shopify.country) return;
  if (/bot|crawl|spider|slurp|google|bing|yandex|baidu|duckduck|facebookexternal|lighthouse|pagespeed|headless|preview/i.test(agent)) return;

  // في السلة منتجات: زائر بدأ الشراء، فلا تُغيَّر دولته فجأة
  const cartCount = document.querySelector('cart-count [aria-hidden="true"]');
  if (cartCount && parseInt(cartCount.textContent, 10) > 0) {
    remember('cart');
    return;
  }

  // دولة في الرابط: اختيار صريح، فلا تُغيَّر
  if (new URLSearchParams(location.search).has('country')) {
    remember('manual');
    return;
  }

  try {
    if (sessionStorage.getItem(AUTO_KEY)) return;
  } catch (e) {}

  const root = (shopify.routes && shopify.routes.root) || '/';
  const url = `${root}browsing_context_suggestions.json?country%5Benabled%5D=true&country%5Bexclude%5D=${encodeURIComponent(shopify.country)}`;

  let data;
  try {
    const response = await fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(String(response.status));
    data = await response.json();
  } catch (e) {
    // خطأ في الشبكة: تُعاد المحاولة في جلسة لاحقة
    try {
      sessionStorage.setItem(AUTO_KEY, 'retry');
    } catch (e2) {}
    return;
  }

  // اختار الزائر دولته أثناء الانتظار
  if (storage.getItem(AUTO_KEY)) return;
  remember('done');

  const handle = data?.suggestions?.[0]?.parts?.country?.handle;
  if (!handle || handle === shopify.country) return;

  // الدول في القائمة المشتركة، والمنتقيات فيها دولتها الحالية وحدها حتى تُفتح
  const has = (root) => root?.querySelector(`[role="option"][value="${CSS.escape(handle)}"]`);
  const forms = Array.from(document.querySelectorAll(FORM_SELECTOR));
  const form = has(document.getElementById(OPTIONS_ID)?.content) ? forms[0] : forms.find(has);
  if (!form) return; // دولة لا نشحن إليها: تبقى الدولة الحالية

  const returnTo = form.querySelector('input[name="return_to"]');
  if (returnTo) returnTo.value = location.pathname + location.search + location.hash;

  // الحقل المخفي للدولة في النموذج، واللغة تبقى كما هي
  const input = form.querySelector('input[type="hidden"][name="country_code"]');
  if (!input) return;
  input.value = handle;
  // لا يُقترح تغيير اللغة في الزيارة التي تغيّرت فيها الدولة تلقائيًا
  try {
    sessionStorage.setItem(LANG_KEY, 'wait');
  } catch (e) {}
  form.submit();
  return true;
};

/* ---------------- اقتراح اللغة (STR-85) ----------------
   بطاقة صغيرة أسفل الصفحة بلغة الزائر المقترحة، بزر للانتقال وزر للإغلاق. الاقتراح من Shopify (browsing_context_suggestions)
   بحسب لغة الجهاز الأولى: الإنجليزية لجهاز بالإنجليزية على الصفحة العربية، والعربية لجهاز بالعربية على الإنجليزية.
   تظهر في الزيارة الأولى وحدها وتبقى في صفحاتها حتى يضغط الزائر أحد الزرين، ولا تعود بعد ذلك ولا في زيارة لاحقة.
   ولا تظهر إن غيّر الزائر اللغة بنفسه، ولا في زيارة تغيّرت فيها الدولة تلقائيًا (تظهر في التي بعدها). */

const LANG_KEY = 'sedr-lang-suggest';

const LANG_TEXT = {
  en: { dir: 'ltr', text: 'View this site in English?', go: 'Switch to English', close: 'Close', label: 'Language suggestion' },
  ar: { dir: 'rtl', text: 'تصفّح الموقع بالعربية؟', go: 'الانتقال إلى العربية', close: 'إغلاق', label: 'اقتراح اللغة' },
};

const LANG_CSS = `
.sedr-lang-suggest { position: fixed; z-index: 31; left: 1rem; bottom: calc(1rem + var(--sedr-lang-lift, 0px)); display: flex; flex-wrap: wrap; align-items: center; gap: .75rem 1rem; box-sizing: border-box; max-width: min(30rem, calc(100vw - 2rem)); padding: 1rem 1rem 1rem 1.25rem; border: 1px solid rgb(var(--text-color) / .12); border-radius: var(--rounded-sm); background: rgb(var(--dialog-background)); color: rgb(var(--text-color)); box-shadow: 0 8px 24px rgb(0 0 0 / .12); transition: opacity .2s ease, transform .2s ease; }
.sedr-lang-suggest[dir="rtl"] { padding: 1rem 1.25rem 1rem 1rem; }
.sedr-lang-suggest[hidden] { display: none; }
.sedr-lang-suggest.is-entering { opacity: 0; transform: translateY(8px); }
.sedr-lang-suggest__text { flex: 1 1 10rem; margin: 0; font-weight: 600; }
.sedr-lang-suggest__actions { display: flex; align-items: center; gap: .5rem; }
.sedr-lang-suggest__close { display: grid; place-items: center; width: 2.5rem; height: 2.5rem; padding: 0; border: 0; border-radius: 50%; background: none; color: inherit; cursor: pointer; }
.sedr-lang-suggest__close:hover { background: rgb(var(--text-color) / .06); }
@media (max-width: 699px) { .sedr-lang-suggest { right: 1rem; max-width: none; } }
@media (prefers-reduced-motion: reduce) { .sedr-lang-suggest { transition: none; } }
`;

const session = {
  get() {
    try {
      return sessionStorage.getItem(LANG_KEY);
    } catch (e) {
      return 'blocked';
    }
  },
  set(value) {
    try {
      sessionStorage.setItem(LANG_KEY, value);
    } catch (e) {}
  },
};

const rememberLang = (value) => {
  try {
    storage?.setItem(LANG_KEY, value);
  } catch (e) {}
};

const switchLanguage = (code) => {
  const source = document.querySelector('form[action$="/localization"]');
  if (!source) return;
  const form = document.createElement('form');
  form.method = 'post';
  form.action = source.action;
  form.hidden = true;
  const returnTo = source.querySelector('input[name="return_to"]')?.value || location.pathname;
  const fields = { form_type: 'localization', utf8: '✓', _method: 'put', return_to: returnTo + location.search, locale_code: code };
  Object.entries(fields).forEach(([name, value]) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
};

const showSuggestion = (code) => {
  const t = LANG_TEXT[code];
  if (!t || document.querySelector('.sedr-lang-suggest')) return;

  if (!document.getElementById('sedr-lang-suggest-css')) {
    const style = document.createElement('style');
    style.id = 'sedr-lang-suggest-css';
    style.textContent = LANG_CSS;
    document.head.appendChild(style);
  }

  const card = document.createElement('aside');
  card.className = 'sedr-lang-suggest is-entering';
  card.lang = code;
  card.dir = t.dir;
  card.setAttribute('aria-label', t.label);
  card.innerHTML = `<p class="sedr-lang-suggest__text"></p>
    <div class="sedr-lang-suggest__actions">
      <button type="button" class="button button--sm sedr-lang-suggest__go"></button>
      <button type="button" class="sedr-lang-suggest__close"><svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M1 1l12 12M13 1L1 13" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg></button>
    </div>`;
  card.querySelector('.sedr-lang-suggest__text').textContent = t.text;
  card.querySelector('.sedr-lang-suggest__go').textContent = t.go;
  card.querySelector('.sedr-lang-suggest__close').setAttribute('aria-label', t.close);

  const close = () => {
    rememberLang('dismissed');
    session.set('done');
    card.remove();
    bar && observer.disconnect();
  };
  card.querySelector('.sedr-lang-suggest__go').addEventListener('click', () => {
    rememberLang('accepted');
    session.set('done');
    switchLanguage(code);
  });
  card.querySelector('.sedr-lang-suggest__close').addEventListener('click', close);
  card.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') close();
  });

  // ترتفع فوق شريط «أضف إلى السلة» الثابت في صفحة المنتج متى ظهر تحتها
  const bar = document.querySelector('product-quick-add');
  const lift = () => {
    let h = 0;
    if (bar && bar.classList.contains('is-visible') && getComputedStyle(bar).position === 'fixed') {
      const r = bar.getBoundingClientRect(), c = card.getBoundingClientRect();
      if (r.left < c.right && r.right > c.left) h = bar.offsetHeight + parseFloat(getComputedStyle(bar).bottom) + 12;
    }
    card.style.setProperty('--sedr-lang-lift', (Number.isFinite(h) ? h : 0) + 'px');
  };
  const observer = new MutationObserver(lift);
  if (bar) {
    observer.observe(bar, { attributes: true, attributeFilter: ['class'] });
    window.addEventListener('resize', lift, { passive: true });
  }

  document.body.appendChild(card);
  lift();
  requestAnimationFrame(() => requestAnimationFrame(() => card.classList.remove('is-entering')));
};

const suggestLanguage = async () => {
  const shopify = window.Shopify || {};
  const agent = navigator.userAgent || '';

  if (!storage || !shopify.locale) return;
  if (navigator.webdriver || shopify.designMode) return;
  if (/bot|crawl|spider|slurp|google|bing|yandex|baidu|duckduck|facebookexternal|lighthouse|pagespeed|headless|preview/i.test(agent)) return;

  const state = session.get();
  if (state === 'blocked' || state === 'wait' || state === 'done') return;

  const saved = storage.getItem(LANG_KEY);
  // ظهرت في زيارة سابقة ولم يضغط الزائر شيئًا: لا تعود
  if (saved && !(saved === 'shown' && state && state.startsWith('open:'))) return;

  let code = state && state.startsWith('open:') ? state.slice(5) : '';
  if (!code) {
    const root = (shopify.routes && shopify.routes.root) || '/';
    const url = `${root}browsing_context_suggestions.json?language%5Benabled%5D=true&language%5Bexclude%5D=${encodeURIComponent(shopify.locale)}`;
    try {
      const response = await fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const data = await response.json();
      code = (data?.suggestions || []).map((s) => s?.parts?.language?.handle).find(Boolean) || '';
    } catch (e) {
      return;
    }
    // لا اقتراح في هذه الزيارة: لا يُسأل Shopify مرة أخرى فيها
    if (!code || code === shopify.locale || !LANG_TEXT[code]) {
      session.set('done');
      return;
    }
    if (storage.getItem(LANG_KEY)) return;
    rememberLang('shown');
    session.set('open:' + code);
  }
  if (code === shopify.locale) {
    session.set('done');
    return;
  }

  showSuggestion(code);
};

if (!window.__sedrCountryAuto) {
  window.__sedrCountryAuto = true;

  // اختيار الزائر بنفسه يوقف التحديد التلقائي نهائيًا
  document.addEventListener(
    'submit',
    (event) => {
      if (!(event.target instanceof HTMLFormElement)) return;
      // اختيار الزائر لغته بنفسه يوقف اقتراح اللغة
      const locale = event.target.querySelector('input[name="locale_code"]');
      if (locale && event.target.matches('form[action$="/localization"]') && locale.value !== window.Shopify?.locale) {
        rememberLang('manual');
      }
      if (!event.target.matches(FORM_SELECTOR)) return;
      // تغيير اللغة وحدها ليس اختيارًا للدولة
      const combined = event.target.closest('sedr-country-picker')?.combined;
      if (combined && combined.country.value === combined.initialCountry) return;
      remember('manual');
    },
    true
  );

  autoDetect().then((redirected) => {
    if (!redirected) suggestLanguage();
  });
}
